import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { FileText, DollarSign, ListChecks, ScrollText, PenLine, Image as ImageIcon, Plus, Trash2, RefreshCw, Loader2, Download, ExternalLink, Save } from 'lucide-react';
import { toast } from 'sonner';
import { ClauseSection } from './ClauseSection';
import { QuoteAIReview } from './QuoteAIReview';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LIB_ADMIN = ['super_admin', 'office_admin', 'project_manager_estimator', 'fabrication_manager'];
const money = (v) => new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(Number(v) || 0);

const Section = ({ icon: Icon, title, children, right, testId }) => (
  <Card className="bg-white border-slate-200" data-testid={testId}>
    <CardHeader className="bg-gradient-to-r from-blue-50 to-slate-50 border-b border-slate-200 py-3">
      <div className="flex items-center justify-between">
        <CardTitle className="text-slate-900 flex items-center text-base"><Icon className="h-5 w-5 mr-2 text-blue-600" />{title}</CardTitle>
        {right}
      </div>
    </CardHeader>
    <CardContent className="p-5">{children}</CardContent>
  </Card>
);

/** Customer-facing quotation document for a saved 365Ops quote. */
export default function QuoteDocumentTab({ quoteId, user }) {
  const [doc, setDoc] = useState(null);
  const [quote, setQuote] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(null);
  const [sigOpen, setSigOpen] = useState(false);
  const [sigInfo, setSigInfo] = useState(null);
  const [imgCaption, setImgCaption] = useState('');
  const sigRef = useRef(null);
  const fileRef = useRef(null);
  const canEditLibrary = LIB_ADMIN.includes(user?.role);
  const token = localStorage.getItem('token');

  useEffect(() => {
    (async () => {
      try {
        const [d, s] = await Promise.all([axios.get(`${API}/quote-doc/${quoteId}`), axios.get(`${API}/quote-doc/my-signature`)]);
        setDoc(d.data.document); setQuote(d.data.quote); setSigInfo(s.data);
      } catch (e) { toast.error(e.response?.data?.detail || 'Could not load the quote document'); }
    })();
  }, [quoteId]);

  const patch = useCallback((p) => { setDoc((d) => ({ ...d, ...p })); setDirty(true); }, []);

  const save = useCallback(async (silent = false) => {
    if (!doc) return null;
    setSaving(true);
    try {
      const r = await axios.put(`${API}/quote-doc/${quoteId}`, doc);
      setDoc((d) => ({ ...r.data, images: d.images }));
      setDirty(false);
      if (!silent) toast.success('Quote document saved');
      return r.data;
    } catch (e) { toast.error('Could not save the document'); return null; } finally { setSaving(false); }
  }, [doc, quoteId]);

  // autosave 2.5s after the last edit
  useEffect(() => {
    if (!dirty) return undefined;
    const t = setTimeout(() => save(true), 2500);
    return () => clearTimeout(t);
  }, [dirty, doc, save]);

  const pricingTotal = useMemo(() => (doc?.pricing_rows || []).reduce((s, r) => s + (Number(r.price) || 0), 0), [doc]);
  const estimateSell = (Number(quote?.subtotal) || 0) + (Number(quote?.markup_amount) || 0);

  const rerollPricing = async () => {
    try {
      const r = await axios.post(`${API}/quote-doc/${quoteId}/auto-pricing`);
      patch({ pricing_rows: r.data.rows });
      toast.success(`Pricing re-rolled from ${r.data.rows.length} task group${r.data.rows.length === 1 ? '' : 's'}`);
    } catch (e) { toast.error('Could not roll pricing from the estimate'); }
  };
  const setRow = (i, p) => patch({ pricing_rows: doc.pricing_rows.map((r, idx) => (idx === i ? { ...r, ...p, source: 'manual' } : r)) });

  const uploadImage = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file); fd.append('caption', imgCaption);
    try {
      const r = await axios.post(`${API}/quote-doc/${quoteId}/images`, fd);
      setDoc((d) => ({ ...d, images: [...(d.images || []), r.data] }));
      setImgCaption('');
      toast.success('Photo added to the scope page');
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
  };
  const removeImage = async (id) => {
    await axios.delete(`${API}/quote-doc/${quoteId}/images/${id}`);
    setDoc((d) => ({ ...d, images: (d.images || []).filter((i) => i.id !== id) }));
  };

  const applyAddition = (a) => {
    const key = `${a.kind}s`;
    patch({ [key]: [...(doc[key] || []), { id: `ai-${a.id}`, text: a.text, library_id: a.library_id || null }] });
  };
  const applyRewrite = (r) => {
    if (r.kind === 'scope') {
      const items = (doc.scope_items || []).map((s) => (s === r.original ? r.suggested : s));
      patch(items.some((s, i) => s !== doc.scope_items[i]) ? { scope_items: items } : { scope_intro: (doc.scope_intro || '').replace(r.original, r.suggested) });
      return;
    }
    const key = `${r.kind}s`;
    let hit = false;
    const list = (doc[key] || []).map((it) => { if (!hit && (it.id === r.id || it.text === r.original)) { hit = true; return { ...it, text: r.suggested, library_id: null }; } return it; });
    patch({ [key]: hit ? list : [...list, { id: `ai-${r.rid}`, text: r.suggested, library_id: null }] });
  };

  const previewPdf = async () => {
    setPdfBusy('preview');
    try {
      await save(true);
      const r = await axios.post(`${API}/quote-doc/${quoteId}/pdf/preview`, {}, { responseType: 'blob' });
      window.open(URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank');
    } catch (e) { toast.error('Could not build the PDF preview'); } finally { setPdfBusy(null); }
  };
  const generatePdf = async () => {
    setPdfBusy('generate');
    try {
      await save(true);
      const r = await axios.post(`${API}/quote-doc/${quoteId}/pdf`, {}, { timeout: 120000 });
      setDoc((d) => ({ ...d, pdfs: [...(d.pdfs || []), r.data] }));
      if (r.data.drive_url) toast.success(r.data.folder_created ? r.data.note : `Saved ${r.data.filename} to the quote's Drive folder (Workbooks & Quotes)`);
      else toast.warning(r.data.note || 'PDF generated (not filed to Drive)');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not generate the PDF'); } finally { setPdfBusy(null); }
  };
  const downloadPdf = async (p) => {
    const r = await axios.get(`${API}/quote-doc/${quoteId}/pdf/${p.id}`, { responseType: 'blob' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(r.data); a.download = p.filename; a.click();
  };

  const saveSignature = async () => {
    const sig = sigRef.current && !sigRef.current.isEmpty() ? sigRef.current.getTrimmedCanvas().toDataURL('image/png') : sigInfo?.signature || null;
    try {
      await axios.put(`${API}/quote-doc/my-signature`, { signature: sig, job_title: sigInfo?.job_title || '', work_extension: sigInfo?.work_extension || '', cell_phone: sigInfo?.cell_phone || '' });
      setSigInfo((s) => ({ ...s, signature: sig })); setSigOpen(false);
      toast.success('Your signature block is saved for all your quotes');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save the signature'); }
  };

  if (!doc) return <div className="text-slate-700 flex items-center gap-2 p-6"><Loader2 className="h-4 w-4 animate-spin" />Loading quote document…</div>;

  return (
    <div className="space-y-5" data-testid="quote-document-tab">
      {/* ---- header controls ---- */}
      <Card className="bg-white border-slate-200">
        <CardContent className="p-4 grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
          <div>
            <Label className="text-xs text-slate-600">Quote type</Label>
            <select value={doc.quote_type} onChange={(e) => patch({ quote_type: e.target.value })} className="w-full h-9 rounded-md border border-slate-300 text-sm px-2 bg-white" data-testid="doc-quote-type">
              <option value="millwright">Millwright</option>
              <option value="fabrication">Fabrication</option>
            </select>
          </div>
          <div><Label className="text-xs text-slate-600">Revision</Label><Input value={doc.revision || ''} onChange={(e) => patch({ revision: e.target.value })} placeholder="R1" className="h-9" data-testid="doc-revision" /></div>
          <div><Label className="text-xs text-slate-600">Issue date</Label><Input type="date" value={doc.issue_date || ''} onChange={(e) => patch({ issue_date: e.target.value })} className="h-9" data-testid="doc-issue-date" /></div>
          <div className="md:col-span-2"><Label className="text-xs text-slate-600">Account manager</Label><Input value={doc.account_manager || ''} onChange={(e) => patch({ account_manager: e.target.value })} placeholder={quote?.estimator || 'Estimator'} className="h-9" data-testid="doc-account-manager" /></div>
          <label className="flex items-center gap-2 text-sm text-slate-700 h-9"><input type="checkbox" checked={!!doc.budgetary} onChange={(e) => patch({ budgetary: e.target.checked })} data-testid="doc-budgetary" />Budgetary</label>
          <span className={`text-xs h-9 flex items-center ${saving ? 'text-blue-600' : dirty ? 'text-amber-600' : 'text-green-700'}`} data-testid="doc-save-state">
            {saving ? 'Saving…' : dirty ? 'Unsaved changes' : 'Saved'}
          </span>
        </CardContent>
      </Card>

      {/* ---- AI ---- */}
      <QuoteAIReview quoteId={quoteId} document={doc} review={doc.ai_review} onReview={(r) => setDoc((d) => ({ ...d, ai_review: r }))} onAdd={applyAddition} onRewrite={applyRewrite} />

      {/* ---- scope ---- */}
      <Section icon={FileText} title="Project Scope" testId="doc-scope">
        <Label className="text-xs text-slate-600">Description of work to be performed</Label>
        <Textarea value={doc.scope_intro || ''} onChange={(e) => patch({ scope_intro: e.target.value })} rows={4} placeholder="365 Industrial Solutions will supply labour, supervision and equipment to…" className="mb-4" data-testid="doc-scope-intro" />
        <div className="flex items-center justify-between mb-1">
          <Label className="text-xs text-slate-600">Scope items</Label>
          <Button size="sm" variant="outline" onClick={() => patch({ scope_items: [...(doc.scope_items || []), ''] })} data-testid="doc-scope-add"><Plus className="h-4 w-4 mr-1" />Item</Button>
        </div>
        <ul className="space-y-1.5 mb-4">
          {(doc.scope_items || []).map((s, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="mt-2.5 text-slate-500">•</span>
              <Textarea value={s} onChange={(e) => patch({ scope_items: doc.scope_items.map((x, j) => (j === i ? e.target.value : x)) })} rows={1} autoFocus={!s} className="min-h-[36px] py-1.5 text-sm flex-1 resize-none" data-testid={`doc-scope-item-${i}`} />
              <button onClick={() => patch({ scope_items: doc.scope_items.filter((_, j) => j !== i) })} className="mt-1.5 p-1 text-slate-500 hover:text-red-600" data-testid={`doc-scope-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
            </li>
          ))}
        </ul>
        <div className="border-t pt-3">
          <Label className="text-xs text-slate-600 flex items-center gap-1"><ImageIcon className="h-3.5 w-3.5" />Drawings / photos (optional)</Label>
          <div className="flex flex-wrap gap-3 mt-2">
            {(doc.images || []).map((im) => (
              <div key={im.id} className="relative w-40" data-testid={`doc-image-${im.id}`}>
                <img src={`${API}/quote-doc/images/${im.id}?token=${token}`} alt={im.caption || im.filename} className="w-40 h-28 object-cover rounded border" />
                <p className="text-[11px] text-slate-500 truncate mt-0.5">{im.caption || im.filename}</p>
                <button onClick={() => removeImage(im.id)} className="absolute -top-2 -right-2 bg-white rounded-full border p-0.5 text-slate-500 hover:text-red-600" data-testid={`doc-image-remove-${im.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-2">
            <Input value={imgCaption} onChange={(e) => setImgCaption(e.target.value)} placeholder="Caption (optional)" className="h-9 max-w-xs" data-testid="doc-image-caption" />
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { uploadImage(e.target.files?.[0]); e.target.value = ''; }} data-testid="doc-image-input" />
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="doc-image-upload"><Plus className="h-4 w-4 mr-1" />Add photo</Button>
          </div>
        </div>
      </Section>

      {/* ---- pricing ---- */}
      <Section icon={DollarSign} title="Pricing" testId="doc-pricing"
        right={<Button size="sm" variant="outline" onClick={rerollPricing} data-testid="doc-pricing-reroll"><RefreshCw className="h-4 w-4 mr-1" />Re-roll from estimate</Button>}>
        <Input value={doc.pricing_note || ''} onChange={(e) => patch({ pricing_note: e.target.value })} className="h-9 mb-3 text-sm" data-testid="doc-pricing-note" />
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase text-slate-500"><th className="pb-1">Item / Task description</th><th className="pb-1 w-40 text-right">Price</th><th className="w-8" /></tr></thead>
          <tbody>
            {(doc.pricing_rows || []).map((r, i) => (
              <tr key={r.id} data-testid={`doc-price-row-${i}`}>
                <td className="py-1 pr-2"><Input value={r.description} onChange={(e) => setRow(i, { description: e.target.value })} className="h-9" data-testid={`doc-price-desc-${i}`} /></td>
                <td className="py-1"><Input type="number" step="0.01" value={r.price} onChange={(e) => setRow(i, { price: e.target.value })} className="h-9 text-right" data-testid={`doc-price-amount-${i}`} /></td>
                <td className="py-1 pl-1"><button onClick={() => patch({ pricing_rows: doc.pricing_rows.filter((_, j) => j !== i) })} className="p-1 text-slate-500 hover:text-red-600" data-testid={`doc-price-remove-${i}`}><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t font-semibold text-slate-900">
              <td className="pt-2">Total Project Cost</td>
              <td className="pt-2 text-right" data-testid="doc-price-total">{money(pricingTotal)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
        <div className="flex items-center justify-between mt-2">
          <Button size="sm" variant="outline" onClick={() => patch({ pricing_rows: [...(doc.pricing_rows || []), { id: `row-${Date.now()}`, description: '', price: 0, source: 'manual' }] })} data-testid="doc-price-add"><Plus className="h-4 w-4 mr-1" />Row</Button>
          <p className={`text-xs ${Math.abs(pricingTotal - estimateSell) > 0.5 ? 'text-amber-600' : 'text-slate-500'}`} data-testid="doc-price-compare">
            Estimate sell total (cost + markup): {money(estimateSell)}{Math.abs(pricingTotal - estimateSell) > 0.5 ? ` — differs by ${money(pricingTotal - estimateSell)}` : ' ✓'}
          </p>
        </div>
        <Input value={doc.pricing_footnote || ''} onChange={(e) => patch({ pricing_footnote: e.target.value })} placeholder="Footnote under the table (optional)" className="h-9 mt-3 text-sm" data-testid="doc-pricing-footnote" />
      </Section>

      {/* ---- inclusions / exclusions / clarifications ---- */}
      <Section icon={ListChecks} title="Inclusions / Exclusions / Clarifications" testId="doc-clauses">
        <Input value={doc.inclusions_note || ''} onChange={(e) => patch({ inclusions_note: e.target.value })} placeholder="Optional intro line for this page" className="h-9 mb-4 text-sm" data-testid="doc-inclusions-note" />
        <div className="space-y-6">
          <ClauseSection kind="inclusion" items={doc.inclusions || []} onChange={(v) => patch({ inclusions: v })} quoteType={doc.quote_type} canEditLibrary={canEditLibrary} />
          <ClauseSection kind="exclusion" items={doc.exclusions || []} onChange={(v) => patch({ exclusions: v })} quoteType={doc.quote_type} canEditLibrary={canEditLibrary} />
          <ClauseSection kind="clarification" items={doc.clarifications || []} onChange={(v) => patch({ clarifications: v })} quoteType={doc.quote_type} canEditLibrary={canEditLibrary} hint="Standard lines are ticked on by default — edit wording per quote without touching the library." />
        </div>
      </Section>

      {/* ---- terms ---- */}
      <Section icon={ScrollText} title="Terms" testId="doc-terms">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[['exchange_rate', 'Exchange Rate'], ['fob', 'FOB / Location'], ['payment_terms', 'Payment Terms'], ['lead_time', 'Lead Time'], ['warranty', 'Warranty']].map(([k, label]) => (
            <div key={k} className={k === 'payment_terms' ? 'md:col-span-2' : ''}>
              <Label className="text-xs text-slate-600">{label}</Label>
              <Input value={doc.terms?.[k] || ''} onChange={(e) => patch({ terms: { ...doc.terms, [k]: e.target.value } })} className="h-9" data-testid={`doc-term-${k}`} />
            </div>
          ))}
        </div>
      </Section>

      {/* ---- acceptance ---- */}
      <Section icon={PenLine} title="Acceptance" testId="doc-acceptance">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          {[['company', 'Customer company'], ['name', 'Signatory name'], ['title', 'Signatory title']].map(([k, label]) => (
            <div key={k}><Label className="text-xs text-slate-600">{label}</Label><Input value={doc.acceptance?.[k] || ''} onChange={(e) => patch({ acceptance: { ...doc.acceptance, [k]: e.target.value } })} className="h-9" data-testid={`doc-acc-${k}`} /></div>
          ))}
        </div>
        <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-slate-800">Your signature block (estimator)</p>
              <p className="text-xs text-slate-500">{sigInfo?.full_name}{sigInfo?.job_title ? ` I ${sigInfo.job_title}` : ''} · {sigInfo?.email}{sigInfo?.signature ? ' · signature on file' : ' · no signature yet'}</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => setSigOpen((o) => !o)} data-testid="doc-sig-toggle">{sigOpen ? 'Close' : 'Edit'}</Button>
          </div>
          {sigOpen && (
            <div className="mt-3 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div><Label className="text-xs text-slate-600">Job title</Label><Input value={sigInfo?.job_title || ''} onChange={(e) => setSigInfo((s) => ({ ...s, job_title: e.target.value }))} placeholder="Millwright Estimating Manager" className="h-9" data-testid="doc-sig-title" /></div>
                <div><Label className="text-xs text-slate-600">Cell</Label><Input value={sigInfo?.cell_phone || ''} onChange={(e) => setSigInfo((s) => ({ ...s, cell_phone: e.target.value }))} className="h-9" data-testid="doc-sig-cell" /></div>
                <div><Label className="text-xs text-slate-600">Office extension</Label><Input value={sigInfo?.work_extension || ''} onChange={(e) => setSigInfo((s) => ({ ...s, work_extension: e.target.value }))} placeholder="430" className="h-9" data-testid="doc-sig-ext" /></div>
              </div>
              <div className="flex items-start gap-4 flex-wrap">
                <div className="bg-white border rounded" data-testid="doc-sig-canvas"><SignatureCanvas ref={sigRef} penColor="black" canvasProps={{ width: 420, height: 130 }} /></div>
                {sigInfo?.signature && <div><p className="text-xs text-slate-500 mb-1">On file</p><img src={sigInfo.signature} alt="signature" className="h-16 border rounded bg-white" /></div>}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => sigRef.current?.clear()}>Clear</Button>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={saveSignature} data-testid="doc-sig-save">Save signature block</Button>
              </div>
            </div>
          )}
        </div>
      </Section>

      {/* ---- PDF ---- */}
      <Card className="bg-white border-slate-200" data-testid="doc-pdf">
        <CardContent className="p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h4 className="font-semibold text-slate-900">Quotation PDF</h4>
              <p className="text-xs text-slate-500">Title page · Standard T&amp;Cs · Scope · Pricing · Inclusions/Exclusions · Terms · Acceptance — filed to the quote's Drive folder.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => save(false)} disabled={saving} data-testid="doc-save-btn"><Save className="h-4 w-4 mr-1" />{saving ? 'Saving…' : dirty ? 'Save' : 'Saved'}</Button>
              <Button variant="outline" onClick={previewPdf} disabled={!!pdfBusy} data-testid="doc-pdf-preview">{pdfBusy === 'preview' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileText className="h-4 w-4 mr-1" />}Preview PDF</Button>
              <Button onClick={generatePdf} disabled={!!pdfBusy} className="bg-blue-600 hover:bg-blue-700" data-testid="doc-pdf-generate">{pdfBusy === 'generate' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}Generate &amp; file to Drive</Button>
            </div>
          </div>
          {(doc.pdfs || []).length > 0 && (
            <ul className="mt-4 divide-y text-sm" data-testid="doc-pdf-list">
              {[...doc.pdfs].reverse().map((p) => (
                <li key={p.id} className="py-2 flex items-center justify-between gap-3" data-testid={`doc-pdf-${p.id}`}>
                  <div className="min-w-0">
                    <p className="text-slate-900 truncate">{p.filename}</p>
                    <p className="text-xs text-slate-500">{new Date(p.generated_at).toLocaleString()} · {p.generated_by}{p.drive_url ? ' · in Drive' : ' · not in Drive'}</p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {p.drive_url && <a href={p.drive_url} target="_blank" rel="noreferrer" className="inline-flex items-center text-blue-600 hover:underline text-xs" data-testid={`doc-pdf-drive-${p.id}`}><ExternalLink className="h-3.5 w-3.5 mr-1" />Drive</a>}
                    <button onClick={() => downloadPdf(p)} className="inline-flex items-center text-blue-600 hover:underline text-xs" data-testid={`doc-pdf-download-${p.id}`}><Download className="h-3.5 w-3.5 mr-1" />Download</button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
